import React, { useEffect, useState } from "react";
import axios from "axios";
import { Grid, Typography } from "@material-ui/core";
import Widget from "../../../components/Widget";
import dayjs from "dayjs";
import styled from "styled-components";

const ORG_TYPES = [
  { key: "공공기관", color: "#FF5E98" },
  { key: "기업", color: "#4C97E8" },
  { key: "연구소", color: "#9076CB" },
  { key: "특허법인", color: "#7CCB76" },
  { key: "학교", color: "#FF8962" },
  { key: "기타", color: "#FFB84D" },
];

function toCount(value) {
  return Number(value) || 0;
}

function TodayMemberSummary() {
  const [todayData, setTodayData] = useState(null);

  useEffect(() => {
    const today = dayjs().endOf("day");
    axios
      .get("/api/member/memberGraph?term=1&startDate=" + today)
      .then((response) => {
        const [, totalMember, subscribeMember] = response.data.data;
        const total = totalMember[totalMember.length - 1] || {};
        const plan = subscribeMember[subscribeMember.length - 1] || {};
        setTodayData({ total, plan });
      })
      .catch((error) => {
        console.error("오늘 회원 현황 조회 실패:", error);
      });
  }, []);

  if (!todayData) return null;

  const totalCnt = toCount(todayData.total.전체합);
  const planCnt = toCount(todayData.plan.전체합);
  const freeCnt = Math.max(totalCnt - planCnt, 0);
  const planRate = totalCnt ? Math.round((planCnt / totalCnt) * 100) : 0;
  const freeRate = totalCnt ? Math.round((freeCnt / totalCnt) * 100) : 0;

  const rows = ORG_TYPES.map(({ key, color }) => {
    const total = toCount(todayData.total[key]);
    const plan = toCount(todayData.plan[key]);
    const free = Math.max(total - plan, 0);
    return { key, color, total, plan, free };
  });

  return (
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Widget>
        <Header>
          <Typography variant="h6" style={{ fontWeight: "bold" }}>
            오늘 회원 현황
          </Typography>
          <DateHint>{dayjs().format("YYYY. MM. DD")} 기준</DateHint>
        </Header>

        <CardRow>
          <StatCard $accent="#536DFE">
            <StatLabel>총 회원</StatLabel>
            <StatValue>{totalCnt.toLocaleString()}</StatValue>
            <StatSub>전체 가입 회원</StatSub>
          </StatCard>
          <StatCard $accent="#2f9e6a">
            <StatLabel>플랜 회원</StatLabel>
            <StatValue>{planCnt.toLocaleString()}</StatValue>
            <StatSub>전체의 {planRate}%</StatSub>
          </StatCard>
          <StatCard $accent="#64748b">
            <StatLabel>무료 회원</StatLabel>
            <StatValue>{freeCnt.toLocaleString()}</StatValue>
            <StatSub>전체의 {freeRate}%</StatSub>
          </StatCard>
        </CardRow>

        <TableWrap>
          <SummaryTable>
            <thead>
              <tr>
                <th>기관 유형</th>
                <th>총 회원</th>
                <th>플랜 회원</th>
                <th>무료 회원</th>
                <th><span style={{ color: '#2f9e6a' }}>플랜</span>·무료 회원 비율</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <th>
                    <OrgName>
                      <OrgDot $color={row.color} />
                      {row.key}
                    </OrgName>
                  </th>
                  <td>{row.total.toLocaleString()}</td>
                  <td>{row.plan.toLocaleString()}</td>
                  <td>{row.free.toLocaleString()}</td>
                  <td>
                    <BarTrack>
                      <BarFill
                        $color="#2f9e6a"
                        $width={row.total ? (row.plan / row.total) * 100 : 0}
                      />
                      <BarFill
                        $color="#94a3b8"
                        $width={row.total ? (row.free / row.total) * 100 : 0}
                      />
                    </BarTrack>
                  </td>
                </tr>
              ))}
            </tbody>
          </SummaryTable>
        </TableWrap>
      </Widget>
    </Grid>
  );
}

export default TodayMemberSummary;

const Header = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 18px;
`

const DateHint = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
`

const CardRow = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 20px;

    @media (max-width: 800px) {
        grid-template-columns: 1fr;
    }
`

const StatCard = styled.div`
    padding: 18px 20px;
    border-radius: 14px;
    background: #f8fafc;
    border: 1px solid #eceff3;
    border-top: 3px solid ${({ $accent }) => $accent};
`

const StatLabel = styled.div`
    font-size: 13px;
    font-weight: 700;
    color: #64748b;
    margin-bottom: 8px;
`

const StatValue = styled.div`
    font-size: 32px;
    font-weight: 800;
    letter-spacing: -1px;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
`

const StatSub = styled.div`
    margin-top: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #94a3b8;
`

const TableWrap = styled.div`
    overflow: hidden;
    border: 1px solid #eceff3;
    border-radius: 14px;
`

const SummaryTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    text-align: center;
    font-variant-numeric: tabular-nums;

    th, td {
        padding: 13px 14px;
        border-bottom: 1px solid #f0f2f5;
        font-size: 15px;
    }

    thead th {
        background: #e8eef8;
        color: #334155;
        font-size: 13px;
        font-weight: 700;
    }

    tbody th {
        text-align: left;
        font-weight: 700;
        color: #1f2937;
    }

    tbody td {
        color: #0f172a;
        font-weight: 600;
    }

    tbody tr:last-child th,
    tbody tr:last-child td {
        border-bottom: none;
    }
`

const OrgName = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`

const OrgDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    flex-shrink: 0;
`

const BarTrack = styled.div`
    display: flex;
    width: 100%;
    height: 8px;
    border-radius: 99px;
    overflow: hidden;
    background: #eef2f7;
`

const BarFill = styled.span`
    display: block;
    height: 100%;
    width: ${({ $width }) => `${$width}%`};
    background: ${({ $color }) => $color};
`
